import React, { useState, useEffect, useMemo, useContext, useRef } from 'react';
import '../AssestmentForm/AssesstmentForm.css';
import ProgressBar from '../../components/ProgressBar/SharedProgressBar';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useSharedForm } from '../../context/SharedFormContext';
import surveyDataTR from '../AssestmentForm/surveyData'; 
import surveyDataEN from '../AssestmentForm/surveyData_en';
import { ValuationContext } from '../../context/ValuationContext';
import { FiInfo } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import './style/sharedPages.css';
import { isDcfOnlyCompanyStage } from '../sectorOptions';



 
const getRandomSentence = (sentences) => {
    if (!sentences || sentences.length === 0) return '';
    return sentences[Math.floor(Math.random() * sentences.length)];
};

const calculateMaxScores = (data) => {
    const maxScores = {};
    data.forEach(section => {
        let sectionMaxScore = 0;
        section.questions.forEach(question => {
            if (question.type === 'radio') {
                const maxOptionValue = Math.max(...question.options.map(opt => opt.value));
                sectionMaxScore += maxOptionValue;
            } else if (question.type === 'checkbox') {
                const sumOfCheckboxValues = question.options.reduce((acc, opt) => acc + opt.value, 0);
                sectionMaxScore += sumOfCheckboxValues;
            }
        });
        maxScores[section.title] = sectionMaxScore;
    });
    return maxScores;
};

const SharedAssessment = () => {
  const { t, i18n } = useTranslation();
  const surveyData = i18n.language === 'en' ? surveyDataEN : surveyDataTR;
  const { valuationData, updateAssessment } = useContext(ValuationContext);
  const navigate = useNavigate();
  const { token } = useParams();
  const [answers, setAnswers] = useState({});
  const [totalScore, setTotalScore] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalConfig, setModalConfig] = useState({ type: 'success', message: '' });
  const maxScoresByCategory = useMemo(() => calculateMaxScores(surveyData), [surveyData]);
  const { sharedFormData, updateSharedAssessment, clearSharedFormData } = useSharedForm();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const dcfAutoSubmitStarted = useRef(false);
  const isDcfOnly = isDcfOnlyCompanyStage(sharedFormData.entrepreneurInfo?.stage);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []); 

  useEffect(() => {
      if (!sharedFormData.entrepreneurInfo || !sharedFormData.financialInputs) {
          navigate(`/shared-form/${token}/entrepreneur-info`);
          return;
      }
      
      if (isInitialLoad && sharedFormData.assessmentAnswers && Object.keys(sharedFormData.assessmentAnswers).length > 0) {
          console.log('📂 Assessment verileri context\'ten yükleniyor:', sharedFormData.assessmentAnswers);
          setAnswers(sharedFormData.assessmentAnswers);
          setIsInitialLoad(false); 
      }
  }, [token, sharedFormData.entrepreneurInfo, sharedFormData.financialInputs, isInitialLoad]);

  useEffect(() => {
      const questionsMap = new Map();
      const sectionMap = new Map();
      
      surveyData.forEach(section => {
        section.questions.forEach(question => {
          questionsMap.set(question.id, question);
          sectionMap.set(question.id, section.title);
        });
      });
      
      const scoresByCategory = {};
      surveyData.forEach(section => {
          scoresByCategory[section.title] = 0;
      });

      for (const questionId in answers) {
        const question = questionsMap.get(questionId);
        const answerValue = answers[questionId];
        const sectionTitle = sectionMap.get(questionId);
        
        if (question && sectionTitle) {
          if (question.type === 'checkbox' && Array.isArray(answerValue)) {
            answerValue.forEach(selectedOptionText => {
              const selectedOption = question.options.find(opt => opt.option === selectedOptionText);
              if (selectedOption) {
                scoresByCategory[sectionTitle] += selectedOption.value;
              }
            });
          } else if (question.type === 'radio') {
            const selectedOption = question.options.find(opt => opt.option === answerValue);
            if (selectedOption) {
              scoresByCategory[sectionTitle] += selectedOption.value;
            }
          }
        }
      }
      
      const newCategoryResults = {};
      let overallScore = 0;
      
      for(const title in scoresByCategory) {
          const score = scoresByCategory[title];
          const maxScore = maxScoresByCategory[title];
          const percentage = maxScore > 0 ? (score / maxScore) * 100 : 0;
          let strength;
          if (percentage >= 67) { strength = i18n.language === 'en' ? 'Strong' : 'Güçlü'; } 
          else if (percentage >= 34) { strength = i18n.language === 'en' ? 'Medium' : 'Orta'; } 
          else { strength = i18n.language === 'en' ? 'Weak' : 'Zayıf'; }
          newCategoryResults[title] = { score, percentage, strength, maxScore };
          overallScore += score;
      }

      setTotalScore(overallScore); 
      
      const maxOverallScore = 3000000;
      const overallPercentage = maxOverallScore > 0 ? (overallScore / maxOverallScore) * 100 : 0;
      
      const resultsForContext = { 
          totalScore: overallScore, 
          overallPercentage, 
          categoryResults: newCategoryResults, 
      };
      
      updateAssessment(answers, resultsForContext);
      updateSharedAssessment(answers, resultsForContext); 
      
  }, [answers, maxScoresByCategory, surveyData, i18n.language]);

  const saveDraft = async () => {
      setIsSaving(true);
      try {
          const email = sharedFormData.entrepreneurInfo?.email;

          if (!email) {
              alert(t('assessment.emailNotFound'));
              navigate(`/shared-form/${token}/entrepreneur-info`);
              return;
          }

          const resultsForContext = { 
              totalScore: totalScore, 
              overallPercentage: valuationData.assessmentResults?.overallPercentage || 0, 
              categoryResults: valuationData.assessmentResults?.categoryResults || {}, 
              investorSummary: valuationData.assessmentResults?.investorSummary || {} 
          };

          updateSharedAssessment(answers, resultsForContext);

          const response = await fetch(`/api/shared-links/save-draft/${token}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                  email,
                  step: 'assessment',
                  data: {
                      answers,
                      results: resultsForContext
                  }
              })
          });

          const resData = await response.json();

          if (response.ok) {
              setModalConfig({ type: 'success', message: t('assessment.saveSuccess') });
              setShowModal(true);
          } else {
              throw new Error(resData.msg || t('assessment.saveError'));
          }
      } catch (error) {
          console.error('Kaydetme hatası:', error);
          setModalConfig({ type: 'error', message: `${t('common.error')}: ${error.message}` });
          setShowModal(true);
      } finally {
          setIsSaving(false);
      }
  };

  const handleBack = () => {
      const resultsForContext = { 
          totalScore: totalScore, 
          overallPercentage: valuationData.assessmentResults?.overallPercentage || 0, 
          categoryResults: valuationData.assessmentResults?.categoryResults || {}, 
          investorSummary: valuationData.assessmentResults?.investorSummary || {} 
      };
      updateSharedAssessment(answers, resultsForContext);
      
      navigate(`/shared-form/${token}/financial-table`);
  };

  const handleInputChange = (questionId, value, type) => {
    setAnswers(prevAnswers => {
      if (type === 'checkbox') {
        const currentSelection = prevAnswers[questionId] || [];
        const newSelection = currentSelection.includes(value)
          ? currentSelection.filter(item => item !== value)
          : [...currentSelection, value];
        return { ...prevAnswers, [questionId]: newSelection };
      }
      return { ...prevAnswers, [questionId]: value };
    });
  };

  const handleSaveAndProceed = async () => {
      const resultsForContext = isDcfOnly ? {
          totalScore: 0,
          overallPercentage: 0,
          categoryResults: {},
          investorSummary: {}
      } : {
          totalScore: totalScore, 
          overallPercentage: valuationData.assessmentResults?.overallPercentage || 0, 
          categoryResults: valuationData.assessmentResults?.categoryResults || {}, 
          investorSummary: valuationData.assessmentResults?.investorSummary || {} 
      };
      updateSharedAssessment(answers, resultsForContext);

      const { entrepreneurInfo, financialInputs, financialResults, assessmentAnswers, assessmentResults } = sharedFormData;
      const submittedAssessmentAnswers = isDcfOnly ? {} : assessmentAnswers;
      const submittedAssessmentResults = isDcfOnly
          ? { totalScore: 0, overallPercentage: 0, categoryResults: {} }
          : assessmentResults;

      if (!entrepreneurInfo || !financialInputs || (!isDcfOnly && (!assessmentAnswers || !assessmentResults))) {
          setModalConfig({ type: 'error',
          message: t('assessment.dataIncomplete') });
          setShowModal(true);
          return;
      }

      const questionsMap = new Map();
      surveyData.forEach(section => {
          section.questions.forEach(question => {
              questionsMap.set(question.id, question);
          });
      });

      const formattedAnswers = Object.keys(submittedAssessmentAnswers || {}).map(questionId => {
          const questionData = questionsMap.get(questionId);
          const answerValue = submittedAssessmentAnswers[questionId];
          let score = 0;
          let type = 'risk';
          let selectedSentence = '';

          if (questionData) {
              if (questionData.type === 'checkbox' && Array.isArray(answerValue)) {
                  let potentialExists = false;
                  let allSentences = [];
                  
                  answerValue.forEach(selectedText => {
                      const optionData = questionData.options.find(opt => opt.option === selectedText);
                      if (optionData) {
                          score += optionData.value;
                          if (optionData.type === 'potential') potentialExists = true;
                          if (optionData.sentences && optionData.sentences.length > 0) {
                              allSentences.push(...optionData.sentences);
                          }
                      }
                  });
                  
                  if (potentialExists) type = 'potential';
                  selectedSentence = getRandomSentence(allSentences);
                  
              } else if (questionData.type === 'radio') {
                  const optionData = questionData.options.find(opt => opt.option === answerValue);
                  if (optionData) {
                      score = optionData.value;
                      type = optionData.type;
                      selectedSentence = getRandomSentence(optionData.sentences);
                  }
              }
          }
          
          return {
              questionId,
              questionText: questionData ? questionData.text : t('assessment.unknownQuestion'),
              answer: answerValue,
              score,
              type,
              selectedSentence
          };
      });

      const categoryResultsArray = Object.entries(submittedAssessmentResults.categoryResults || {}).map(
          ([categoryTitle, data]) => ({
              categoryTitle,
              score: data.score,
              percentage: data.percentage,
              strength: data.strength,
              maxScore: data.maxScore,
          })
      );

      const payload = {
          entrepreneurInfo,
          financialInputs,
          financialResults,
          answers: formattedAnswers,
          categoryResults: categoryResultsArray,
          totalScore: submittedAssessmentResults.totalScore,
          overallPercentage: submittedAssessmentResults.overallPercentage,
      };

      console.log("BACKEND'E GÖNDERİLECEK VERİ:", payload);

      try {
          const response = await fetch(`/api/shared-links/submit-report/${token}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload)
          });

          const data = await response.json();

          if (response.ok && data.success) {
              clearSharedFormData();
              localStorage.removeItem('sharedFormEmail');
              localStorage.removeItem('sharedFormName');
              localStorage.removeItem('sharedFormToken');
              setAnswers({});
              setTotalScore(0);

              localStorage.setItem('sharedFormCompleted', 'true');

              if (data.tokenWarning) {
                  setModalConfig({
                      type: 'warning',
                      message: data.msg || t('assessment.reportSaved'),
                      onClose: () => setShowModal(false)
                  });
              } else {
                  setModalConfig({
                      type: 'success',
                      message: data.msg || t('assessment.reportSaved'),
                      onClose: () => setShowModal(false)
                  });
              }

              setShowModal(true);

              if (isDcfOnly) {
                  window.location.href = `/shared-form/${token}/success`;
              } else {
                  setTimeout(() => {
                      window.location.href = `/shared-form/${token}/success`;
                  }, 2500);
              }

          } else if (response.status === 410) {
              setModalConfig({ 
                  type: 'error', 
                  message: t('assessment.maxSubmissionsReached'),
                  onClose: () => setShowModal(false)
              });
              setShowModal(true);

          } else if (response.status === 409) {
              setModalConfig({ 
                  type: 'error', 
                  message: t('assessment.emailAlreadySubmitted'),
                  onClose: () => setShowModal(false)
              });
              setShowModal(true);

          } else {
              setModalConfig({ 
                  type: 'error', 
                  message: `${t('common.error')}: ${data.msg || t('assessment.reportError')}`,
                  onClose: () => setShowModal(false)
              });
              setShowModal(true);
          }

      } catch (error) {
          console.error('Gönderim hatası:', error);
          setModalConfig({ 
              type: 'error', 
              message: t('assessment.reportError'),
              onClose: () => setShowModal(false)
          });
          setShowModal(true);
      }
  };

  useEffect(() => {
    if (isDcfOnly && !dcfAutoSubmitStarted.current) {
      dcfAutoSubmitStarted.current = true;
      handleSaveAndProceed();
    }
  }, [isDcfOnly]);

  return (
   <div className="shared-assessment-page">
    {showModal && (
      <div 
        className="modal-overlay" 
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className={`modal-icon ${modalConfig.type}`}>
            {modalConfig.type === 'success' ? '✓' : '✕'}
          </div>
          <p className="modal-message">{modalConfig.message}</p>
          
          <button
            className="modal-close-btn"
            onClick={() => {
              setShowModal(false);
              if (modalConfig.onClose) {
                modalConfig.onClose();
              }
            }}
          >
            {modalConfig.type === 'success' ? t('assessment.returnHome') : t('assessment.ok')}
          </button>
        </div>
      </div>
    )}

    <div className="shared-assessment-container">
      <div className="assessment-header">
        {!isDcfOnly && <div className="progress-section">
          <ProgressBar currentStep={3} />
        </div>}
        <h1>{isDcfOnly ? (i18n.language?.startsWith('en') ? 'DCF Valuation' : 'DCF Değerlemesi') : t('assessment.title')}</h1>
        {isDcfOnly && <p>{i18n.language?.startsWith('en') ? 'Berkus assessment is not used for this company stage. Complete the report using the DCF valuation.' : 'Bu şirket aşamasında Berkus değerlendirmesi uygulanmaz. Raporunuz DCF değerlemesiyle oluşturulacaktır.'}</p>}
       
      </div>
      
      <form className="shared-assessment-form">
        {!isDcfOnly && surveyData.map((section) => (
          <div key={section.title} className="form-section">
            <div className="section-header">
              <h2>{section.title}</h2>
            </div>
            <div className="form-section-content">
              {section.questions.map((question) => (
                <div key={question.id} className="question-block">
                 <label className="question-label">
                  {question.text}
                  {question.tooltip && (
                      <span className="tooltip-wrapper">
                          <FiInfo className="tooltip-icon" />
                          <span className="tooltip-text">{question.tooltip}</span>
                      </span>
                  )}
              </label>
                  <div className="options-group">
                    {question.options.map((optionObject, optionIndex) => {
                      const optionId = `${question.id}-${optionIndex}`;
                      const optionText = optionObject.option;
                      return (
                        <label key={optionId} className="option-item">
                          <input
                            type={question.type}
                            id={optionId}
                            name={question.id}
                            value={optionText}
                            checked={
                              question.type === 'checkbox'
                                ? (answers[question.id] || []).includes(optionText)
                                : answers[question.id] === optionText
                            }
                            onChange={() => handleInputChange(question.id, optionText, question.type)}
                          />
                          <span htmlFor={optionId}>{optionText}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </form>
      
      <div className="form-navigation">
        {isDcfOnly ? <div className="nav-buttons-group">
          <button type="button" className="back-button" onClick={handleBack}>← {t('assessment.previous')}</button>
          <button type="button" onClick={handleSaveAndProceed} className="next-button">
            {i18n.language?.startsWith('en') ? 'Create DCF Report' : 'DCF Raporunu Oluştur'} →
          </button>
        </div> : <>
                  <button
                      type="button"
                      className="save-draft-btn"
                      onClick={saveDraft}
                      disabled={isSaving}
                  >
                      {isSaving ? t('assessment.saving') : t('assessment.saveForLater')}
                  </button>

                  <div className="nav-buttons-group">
                    <button
                        type="button"
                        className="back-button"
                        onClick={handleBack}
                    >
                        ← {t('assessment.previous')}
                    </button>
                      <button
                          type="button"
                          onClick={handleSaveAndProceed}
                          className="next-button"
                      >
                          {t('assessment.completeAssessment')} →
                      </button>
                  </div>
        </>}
              </div>
    </div>
  </div>
);
};

export default SharedAssessment;
